import React, { useMemo } from 'react';
import {
  Frame,
  Grid,
  SageClassnames,
  useSageBreakpoint,
} from '@kajabi/sage-react';

interface PropsType {
  userData: any;
  heroImageUrl: string;
}

const SalesEmailPreview = ({ userData, heroImageUrl }: PropsType) => {
  const breakpoints = useSageBreakpoint();

  const salesEmailData = useMemo(() => {
    return userData.ai_data.emails;
  }, [userData]);

  return (
    <Frame
      style={{
        width: breakpoints.lg ? '100%' : '70%',
        border: breakpoints.sm ? '1rem solid #eceeef' : null,
        backgroundColor: breakpoints.sm ? '#eceeef' : null,
        borderRadius: breakpoints.sm ? '32px' : '15px',
        boxShadow: breakpoints.sm ? 'rgba(0, 0, 0, 0.1) 0px 20px 40px' : null,
      }}
      align={Frame.ALIGNMENTS.STRETCH_CENTER}
    >
      <Frame
        gap={Frame.GAPS.NONE}
        className={`container ${
          SageClassnames.SPACERS[breakpoints.md ? 'NONE' : 'LG_TOP']
        }`}
        style={{
          width: [breakpoints.md ? '100%' : '732px'],
          boxShadow: breakpoints.sm ? null : 'rgba(0, 0, 0, 0.1) 0px 20px 40px',
          borderRadius: breakpoints.sm ? '24px' : '15px',
          height: breakpoints.md ? '576px' : '720px',
          overflowY: 'scroll',
          backgroundColor: 'white',
        }}
      >
        {/* Mock window component with 3 buttons */}
        {breakpoints.sm || (
          <Frame
            background="#ECEEEF"
            style={{
              height: '42px',
              borderTopLeftRadius: '15px',
              borderTopRightRadius: '15px',
            }}
          >
            <Frame
              direction={Frame.DIRECTIONS.HORIZONTAL}
              padding={Frame.PADDINGS.SM}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                }}
              ></div>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                  marginLeft: '-15px',
                }}
              ></div>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                  marginLeft: '-15px',
                }}
              ></div>
            </Frame>
          </Frame>
        )}

        {/* Sales email navigation component */}
        {breakpoints.sm && (
          <Frame
            background="#FFFFFF"
            style={{ height: '61px' }}
            direction={Frame.DIRECTIONS.HORIZONTAL}
            padding={Frame.PADDINGS.SM}
          >
            <Frame
              padding={Frame.PADDINGS.XS}
              direction={Frame.DIRECTIONS.HORIZONTAL}
            >
              <div
                style={{
                  width: 87,
                  height: 8,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              ></div>
            </Frame>
            <Frame
              padding={Frame.PADDINGS.NONE}
              align={Frame.ALIGNMENTS.TOP_RIGHT}
              gap={Frame.GAPS.XXS}
            >
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
            </Frame>
          </Frame>
        )}
        <Grid>
          <Grid.Row spacerBelow="sm">
            <Grid.Col size="2">
              {breakpoints.sm || (
                <Frame
                  direction={Frame.DIRECTIONS.VERTICAL}
                  padding={Frame.PADDINGS.LG}
                  gap={Frame.GAPS.XS}
                >
                  <>
                    <div
                      style={{
                        width: 94,
                        height: 12,
                        backgroundColor: '#D3D5D9',
                        borderRadius: '100px',
                      }}
                      className={SageClassnames.SPACERS.XS_BOTTOM}
                    ></div>
                    {Array(3)
                      .fill(true)
                      .map((_: unknown, key: number) => (
                        <React.Fragment key={`sales-preview-image-url-${key}`}>
                          <div
                            style={{
                              width: 85,
                              height: 8,
                              backgroundColor: '#ECEEEF',
                              borderRadius: '100px',
                            }}
                          ></div>
                          <div
                            style={{
                              width: 56,
                              height: 8,
                              backgroundColor: '#ECEEEF',
                              borderRadius: '100px',
                            }}
                          ></div>
                        </React.Fragment>
                      ))}
                  </>
                </Frame>
              )}
            </Grid.Col>
            <Grid.Col size="10">
              <Frame
                style={{ backgroundColor: 'white' }}
                gap={Frame.GAPS.NONE}
                padding={Frame.PADDINGS[breakpoints.sm ? 'NONE' : 'LG']}
              >
                <h6
                  className={`${SageClassnames.TYPE.HEADING_6} ${
                    SageClassnames.SPACERS[breakpoints.sm ? 'MD_LEFT' : 'NONE']
                  } ${
                    SageClassnames.SPACERS[breakpoints.sm ? 'MD_RIGHT' : 'NONE']
                  }`}
                >
                  {salesEmailData.subject}
                </h6>
                <p
                  className={`${SageClassnames.TYPE.BODY_SMALL} ${
                    SageClassnames.SPACERS.SM_BOTTOM
                  } ${
                    SageClassnames.SPACERS[breakpoints.sm ? 'MD_LEFT' : 'NONE']
                  } ${
                    SageClassnames.SPACERS[breakpoints.sm ? 'MD_RIGHT' : 'NONE']
                  }`}
                >
                  from: yourname@example.com
                </p>
                {/* Sales email hero image component */}
                <Frame
                  style={{
                    minHeight: '180px',
                    width: '100%',
                    maxWidth: '600px',
                    backgroundImage: `
                          linear-gradient(rgba(0,0,0,.15), rgba(0,0,0,.15)),
                          url(${heroImageUrl})
                      `,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                    border: '1px solid #ECEEEF',
                  }}
                  padding={Frame.PADDINGS.LG}
                  gap={Frame.GAPS.XS}
                  align={Frame.ALIGNMENTS.CENTER_CENTER}
                ></Frame>
                {/* Formatted sales email component */}
                <Frame
                  style={{
                    width: '100%',
                    maxWidth: '600px',
                    border: breakpoints.sm ? 'none' : '2px solid #ECEEEF',
                    borderTop: '0px',
                    borderBottomLeftRadius: '10px',
                    borderBottomRightRadius: '10px',
                  }}
                  gap={Frame.GAPS.SM}
                  padding={Frame.PADDINGS.MD}
                >
                  <p className={SageClassnames.TYPE.BODY_XSMALL}>
                    {salesEmailData.body.first_sentence}
                  </p>
                  <p className={SageClassnames.TYPE.BODY_XSMALL}>
                    {salesEmailData.body.first_paragraph}
                  </p>
                  <p className={SageClassnames.TYPE.BODY_XSMALL}>
                    {salesEmailData.body.second_paragraph}
                  </p>
                  <div
                    style={{
                      width: 120,
                      height: 28,
                      backgroundColor: '#0072EF',
                      borderRadius: '6px',
                      display: 'flex',
                      alignSelf: 'center',
                    }}
                    className={`${SageClassnames.SPACERS.SM_TOP} ${SageClassnames.SPACERS.SM_BOTTOM}`}
                  >
                    <p
                      style={{
                        fontSize: '12px',
                        color: 'white',
                        margin: 'auto',
                      }}
                    >
                      Purchase now
                    </p>
                  </div>
                  <div>
                    <p className={SageClassnames.TYPE.BODY_XSMALL_BOLD}>
                      Our course offers the following benefits:
                    </p>
                    <ul style={{ paddingLeft: '25px' }}>
                      {salesEmailData.body.bullet_points.map(
                        (bulletPoint: string, key: number) => (
                          <li
                            key={`sales-preview-bullet-points-${key}`}
                            className={SageClassnames.TYPE.BODY_XSMALL}
                          >
                            {bulletPoint}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                  <p className={SageClassnames.TYPE.BODY_XSMALL}>
                    [Consider adding a testemonial here] <br />
                    {salesEmailData.body.call_to_action}
                  </p>
                  <p className={SageClassnames.TYPE.BODY_XSMALL}>
                    {salesEmailData.body.closing_regards.split('\n')[0]} <br />
                    {salesEmailData.body.closing_regards.split('\n')[1]}
                  </p>
                </Frame>
              </Frame>
            </Grid.Col>
          </Grid.Row>
        </Grid>
      </Frame>
    </Frame>
  );
};

export default SalesEmailPreview;
